import type { Block } from "building-types/blocks/blockTypes";
import type { Building } from "building-types/building/buildingTypes";
import { MissingExpressionOptionSetError } from "building-types/errors/buildingGenerationError";
import type { Formula } from "building-types/options/formula";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import type { RWTable } from "common/data/table";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { isFormula } from "doc/formula/formulaParsing";
import { shouldGenerateEntity } from "../utils/entityIsolation";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  doc: { blocks, optionSets, instanceOptionSets, entityRefs, docRefs },
  optionChoices,
  levels,
  entityDisabledByGroup,
  idToParents,
  isolatedEntity,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    levels,
    optionChoices: resolveOptionsInFormula(blocks, definitions, optionChoices),
    blocks,
    entityRefs,
    docRefs,
    entityDisabledByGroup,
    idToParents,
    isolatedEntity,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  levels: "shallow",
  optionChoices: "shallow",
  blocks: "deep",
  entityRefs: "shallow",
  docRefs: "shallow",
  idToParents: "shallow",
  isolatedEntity: "shallow",
});

function generate({
  levels,
  optionChoices,
  blocks,
  entityRefs,
  docRefs,
  entityDisabledByGroup,
  idToParents,
  isolatedEntity,
}: ReturnType<typeof select>): {
  blocks: RWTable<Block>;
  issues: BuildingGenerationIssue[];
} {
  const issues: BuildingGenerationIssue[] = [];

  const lookupOption = (id: string, formula: Formula) => {
    if (optionChoices[id] !== undefined) {
      return optionChoices[id];
    }

    console.warn(`Ignoring formula, invalid option set ${id} found in formula: ${formula.formula}`);
    throw new MissingExpressionOptionSetError(id, formula.formula);
  };

  const blocksActive: RWTable<Block> = {};

  for (const blockId in blocks) {
    const block = blocks[blockId];

    if (!levels[block.levelId]) {
      const entityRef = entityRefs[blockId];
      const docRef = entityRef ? docRefs[entityRef.docRefId] : undefined;

      issues.push({
        type: BuildingGenerationIssueType.BLOCK_MISSING_LEVEL,
        missingLevelId: block.levelId,
        handle: {
          type: "BLOCK",
          id: blockId,
        },
        ...(docRef
          ? {
              docRefId: docRef.id,
              docRefName: docRef.name,
            }
          : {}),
      });
      continue;
    }

    let disabled = block.disabled;

    if (entityDisabledByGroup && entityDisabledByGroup[block.id]) {
      continue;
    }

    if (isFormula(disabled)) {
      try {
        disabled = evalFormulas(disabled, lookupOption, `Block: ${blockId}`, entityDisabledByGroup);
      } catch (e) {
        disabled = false;
      }
    }

    if (disabled || !shouldGenerateEntity(blockId, idToParents, isolatedEntity)) {
      continue;
    }

    blocksActive[blockId] = {
      ...block,
    };
  }

  return { blocks: blocksActive, issues };
}

export default memo(select, generate, compare);
